import { apiClient } from "@/services/apiClient";
import { getDefaultPlatformKeys } from "@/config/platforms";

export interface PublishPostResponse {
  success: boolean;
  postId?: string;
  platforms?: string[];
  publishRecords?: Array<{
    id: string;
    platform: string;
    status: string;
  }>;
  jobExecutionId?: string;
  errors?: string[];
  error?: string;
  /** The prepared media was missing from storage; the post has been queued
   * for re-preparation and will publish automatically once ready. */
  queuedForPreparation?: boolean;
  message?: string;
}

export interface PublishingStatus {
  postId: string;
  overallStatus: "not_published" | "pending" | "publishing" | "published" | "partially_published" | "failed";
  platforms: {
    [key: string]: {
      id: string;
      platform: string;
      status: string;
      platformPostId?: string;
      platformPermalink?: string;
      errorCode?: string;
      errorMessage?: string;
      retryCount?: number;
      publishedAt?: string;
      createdAt?: string;
    };
  };
}

export interface PublishErrorResponse {
  error: string;
  message?: string;
  platform?: string;
  platformPostId?: string;
  platformPermalink?: string;
}

export const publishPostService = async (
  postId: string,
  clientId: string,
  platforms?: string[],
  force?: boolean,
  /**
   * Per-platform posting options collected from a consent screen, keyed by
   * platform (e.g. `{ tiktok: { privacyLevel: "PUBLIC_TO_EVERYONE", ... } }`).
   * Persisted on the publish record so the background job posts exactly what
   * the user agreed to.
   */
  platformOptions?: Record<string, unknown>
): Promise<PublishPostResponse> => {
  try {
    const response = await apiClient.post<PublishPostResponse>(
      `/api/social-media/publish/${postId}`,
      {
        clientId: clientId,
        platforms: platforms || getDefaultPlatformKeys(),
        force: force || false,
        ...(platformOptions ? { platformOptions } : {}),
      }
    );

    return response;
  } catch (error: any) {
    console.error("Failed to publish post:", error);

    // The apiClient interceptor converts errors to simple Error objects
    // Check both error.message and error.response?.data for ALREADY_PUBLISHED
    const errorData = error.response?.data as PublishErrorResponse | undefined;
    const isAlreadyPublished =
      error.message === "ALREADY_PUBLISHED" ||
      errorData?.error === "ALREADY_PUBLISHED";

    if (isAlreadyPublished) {
      const err = new Error(errorData?.message || "Post already published") as any;
      err.code = "ALREADY_PUBLISHED";
      err.platform = errorData?.platform;
      err.platformPostId = errorData?.platformPostId;
      err.platformPermalink = errorData?.platformPermalink;
      throw err;
    }
    throw new Error(errorData?.error || error.message || "Failed to publish post");
  }
};

export const getPublishingStatusService = async (
  postId: string
): Promise<PublishingStatus> => {
  try {
    const response = await apiClient.get<PublishingStatus>(
      `/api/social-media/publish/status/${postId}`
    );

    return response;
  } catch (error: any) {
    console.error("Failed to get publishing status:", error);
    throw new Error(error.response?.data?.error || "Failed to get publishing status");
  }
};

export const retryFailedPublishService = async (
  socialMediaPublishId: string,
  clientId: string,
  postId: string
): Promise<{ success: boolean; socialMediaPublishId?: string; jobExecutionId?: string; error?: string }> => {
  try {
    const response = await apiClient.post(
      `/api/social-media/publish/retry/${socialMediaPublishId}`,
      {
        clientId: clientId,
        postId: postId,
      }
    );

    return response;
  } catch (error: any) {
    console.error("Failed to retry publish:", error);
    throw new Error(error.response?.data?.error || "Failed to retry publish");
  }
};
